// frontend/src/pages/reports/SCSTStudentsReportPage.js
import React, { useState, useEffect } from 'react';
import {
  Table, Card, Space, Button, Select, Spin, Typography, Row, Col,
  Statistic, Divider, Empty, Tag, message
} from 'antd';
import { DownloadOutlined, PrinterOutlined } from '@ant-design/icons';
import { getAllStudents } from '../../services/studentService';
import { getAllClasses } from '../../services/classService';
import { useAcademicYear } from '../../context/AcademicYearContext';

const { Title, Text } = Typography;

const SCSTStudentsReportPage = () => {
  const { currentYear } = useAcademicYear();
  const [students, setStudents] = useState([]);
  const [classes, setClasses] = useState([]);
  const [selectedClass, setSelectedClass] = useState(null);
  const [selectedGender, setSelectedGender] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [filteredStudents, setFilteredStudents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pageSize, setPageSize] = useState(15);
  const [stats, setStats] = useState({ male: 0, female: 0, sc: 0, st: 0 });

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [studentsData, classesData] = await Promise.all([
          getAllStudents(),
          getAllClasses()
        ]);

        // Filter only SC/ST students
        const scstStudents = studentsData.filter(
          s => s.allotted_category === 'SC' || s.allotted_category === 'ST'
        );

        setStudents(scstStudents);
        setClasses(classesData);
        // console.log('SCSTStudentsReportPage - Fetched SC/ST students:', scstStudents);
        // console.log('SCSTStudentsReportPage - Fetched classes:', classesData);

        // Calculate stats for SC/ST students
        const male = scstStudents.filter(s => s.gender === 'Male').length;
        const female = scstStudents.filter(s => s.gender === 'Female').length;
        const sc = scstStudents.filter(s => s.allotted_category === 'SC').length;
        const st = scstStudents.filter(s => s.allotted_category === 'ST').length;

        setStats({ male, female, sc, st });
        // console.log('SCSTStudentsReportPage - Calculated Stats:', { male, female, sc, st });
      } catch (error) {
        console.error('Error loading students:', error);
        message.error('Failed to load SC/ST students');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [currentYear]);

  // Apply filters
  useEffect(() => {
    let filtered = [...students];

    if (selectedClass) {
      filtered = filtered.filter(s => s.class_id === selectedClass);
    }
    if (selectedGender) {
      filtered = filtered.filter(s => s.gender === selectedGender);
    }
    if (selectedCategory) {
      filtered = filtered.filter(s => s.allotted_category === selectedCategory);
    }

    // console.log('SCSTStudentsReportPage - Applied filters:', { selectedClass, selectedGender, selectedCategory });
    // console.log('SCSTStudentsReportPage - Filtered results count:', filtered.length);
    setFilteredStudents(filtered);
  }, [selectedClass, selectedGender, selectedCategory, students]);

  const columns = [
    {
      title: 'Admission No',
      dataIndex: 'admission_no',
      key: 'admission_no',
      width: 120,
    },
    {
      title: 'Student Name',
      dataIndex: 'student_name',
      key: 'student_name',
      filterSearch: true,
    },
    {
      title: 'Class',
      dataIndex: 'class_name',
      key: 'class_name',
      width: 80,
    },
    {
      title: 'Gender',
      dataIndex: 'gender',
      key: 'gender',
      render: (gender) => (
        <Tag color={gender === 'Male' ? 'blue' : gender === 'Female' ? 'pink' : 'default'}>
          {gender || 'N/A'}
        </Tag>
      ),
      width: 80,
    },
    {
      title: 'Category',
      key: 'category',
      render: (_, record) => {
        const cat = record.allotted_category;
        return <Tag color={cat === 'SC' ? 'gold' : 'orange'}>{cat}</Tag>;
      },
      width: 100,
    },
    {
      title: 'Ration Card',
      dataIndex: 'ration_card_type',
      key: 'ration_card_type',
      render: (status) => (
        <Tag color={status === 'BPL' ? 'red' : status === 'APL' ? 'green' : 'default'}>
          {status || 'Not Specified'}
        </Tag>
      ),
      width: 100,
    },
  ];

  const handleExport = () => {
    if (filteredStudents.length === 0) {
      message.warning('No data to export');
      return;
    }

    let title = 'SC/ST Students Report';
    let filters = [];
    if (selectedClass) filters.push(`Class: ${classes.find(c => c.id === selectedClass)?.name}`);
    if (selectedGender) filters.push(`Gender: ${selectedGender}`);
    if (selectedCategory) filters.push(`Category: ${selectedCategory}`);

    const csv = [
      [title],
      ...filters.map(f => [f]),
      [''],
      ['Admission No', 'Student Name', 'Class', 'Gender', 'Category', 'Ration Card Type'],
      ...filteredStudents.map(s => [
        s.admission_no,
        s.student_name,
        s.class_name || '-',
        s.gender || '-',
        s.allotted_category || 'N/A',
        s.ration_card_type || 'Not Specified',
      ])
    ]
      .map(row => row.join(','))
      .join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `scst_students_report.csv`;
    a.click();
  };

  return (
    <div style={{ padding: '24px' }}>
      <Space direction="vertical" style={{ width: '100%' }} size="large">
        <div>
          <Title level={2}>👥 SC/ST Students Report</Title>
          <Text type="secondary">
            View students with Scheduled Caste (SC) and Scheduled Tribe (ST) categories
          </Text>
        </div>

        {/* Summary Statistics */}
        <Row gutter={16}>
          <Col xs={12} sm={8}>
            <Card>
              <Statistic
                title="Total SC/ST Students"
                value={students.length}
                valueStyle={{ color: '#ff7a45' }}
              />
            </Card>
          </Col>
          <Col xs={12} sm={8}>
            <Card>
              <Statistic
                title="Male SC/ST"
                value={stats.male}
                valueStyle={{ color: '#1890ff' }}
              />
            </Card>
          </Col>
          <Col xs={12} sm={8}>
            <Card>
              <Statistic
                title="Female SC/ST"
                value={stats.female}
                valueStyle={{ color: '#ff85c0' }}
              />
            </Card>
          </Col>
          <Col xs={12} sm={8}>
            <Card>
              <Statistic
                title="SC Students"
                value={stats.sc}
                valueStyle={{ color: '#faad14' }}
              />
            </Card>
          </Col>
          <Col xs={12} sm={8}>
            <Card>
              <Statistic
                title="ST Students"
                value={stats.st}
                valueStyle={{ color: '#eb2f96' }}
              />
            </Card>
          </Col>
        </Row>

        <Divider />

        {/* Filters */}
        <Card title="Filters" extra={<Text type="secondary">Select filters to refine the report</Text>}>
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} lg={8}>
              <label>Filter by Class</label>
              <Select
                placeholder="All Classes"
                value={selectedClass}
                onChange={setSelectedClass}
                allowClear
                options={[
                  { label: 'All Classes', value: null },
                  ...classes.map(c => ({
                    label: c.name,
                    value: c.id,
                  }))
                ]}
                style={{ width: '100%' }}
              />
            </Col>
            <Col xs={24} sm={12} lg={8}>
              <label>Filter by Gender</label>
              <Select
                placeholder="All Genders"
                value={selectedGender}
                onChange={setSelectedGender}
                allowClear
                options={[
                  { label: 'Male', value: 'Male' },
                  { label: 'Female', value: 'Female' },
                ]}
                style={{ width: '100%' }}
              />
            </Col>
            <Col xs={24} sm={12} lg={8}>
              <label>Filter by Category</label>
              <Select
                placeholder="All Categories"
                value={selectedCategory}
                onChange={setSelectedCategory}
                allowClear
                options={[
                  { label: 'SC', value: 'SC' },
                  { label: 'ST', value: 'ST' },
                ]}
                style={{ width: '100%' }}
              />
            </Col>
          </Row>
        </Card>

        {/* Results */}
        <Card
          title={`SC/ST Students (${filteredStudents.length} results)`}
          extra={
            <Space>
              <Button
                type="primary"
                icon={<DownloadOutlined />}
                onClick={handleExport}
                disabled={filteredStudents.length === 0}
              >
                Export CSV
              </Button>
              <Button icon={<PrinterOutlined />} onClick={() => window.print()}>
                Print
              </Button>
            </Space>
          }
        >
          {loading ? (
            <Spin />
          ) : filteredStudents.length > 0 ? (
            <Table
              columns={columns}
              dataSource={filteredStudents.map((s, i) => ({ ...s, key: i }))}
              pagination={{
                pageSize: pageSize,
                showSizeChanger: true,
                pageSizeOptions: [10, 20, 50, 100],
                onShowSizeChange: (current, size) => setPageSize(size),
                onChange: (page, size) => setPageSize(size)
              }}
              scroll={{ x: 1000 }}
            />
          ) : (
            <Empty description="No SC/ST students found with selected filters" />
          )}
        </Card>
      </Space>
    </div>
  );
};

export default SCSTStudentsReportPage;
